import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CarouselProps {
  children: React.ReactNode[];
  /** Width of each slide below the `sm` breakpoint, e.g. "w-[82%]". */
  slideClassName?: string;
  /** Classes applied to the track at `sm:` and up that turn it back into a normal grid,
   *  e.g. "sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-5". Swipe/arrows/dots only ever
   *  apply below `sm` — above it this renders as a plain grid. */
  desktopGridClassName: string;
  className?: string;
}

/** Snap-scroll swipeable row on mobile; reverts to a normal CSS grid at `sm:` and up.
 *  Used so card sections don't force a cramped multi-column squeeze on small screens.
 *
 *  Slide classes are cloned directly onto each child (not wrapped in an extra div) —
 *  CSS Grid's `col-span`/`row-span` only apply to *direct* children of the grid
 *  container, so a wrapper div would silently break any child that relies on one. */
const Carousel = ({ children, slideClassName = 'w-[82%]', desktopGridClassName, className = '' }: CarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateEdges = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
    const slideWidth = el.firstElementChild ? (el.firstElementChild as HTMLElement).offsetWidth + 16 : 1;
    setActive(Math.round(el.scrollLeft / slideWidth));
  };

  useEffect(() => {
    updateEdges();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const slideWidth = el.firstElementChild ? (el.firstElementChild as HTMLElement).offsetWidth + 16 : el.clientWidth;
    el.scrollBy({ left: dir * slideWidth, behavior: 'smooth' });
  };

  const scrollTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const slideWidth = el.firstElementChild ? (el.firstElementChild as HTMLElement).offsetWidth + 16 : el.clientWidth;
    el.scrollTo({ left: i * slideWidth, behavior: 'smooth' });
  };

  return (
    <div className={className}>
      <div
        ref={trackRef}
        className={`flex gap-4 overflow-x-auto scroll-smooth pb-2 sm:overflow-visible sm:pb-0 ${desktopGridClassName}`}
        style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none' }}
      >
        {children.map((child, i) => {
          if (!React.isValidElement<{ className?: string; style?: React.CSSProperties }>(child)) return child;
          return React.cloneElement(child, {
            key: child.key ?? i,
            className: `${child.props.className ?? ''} shrink-0 ${slideClassName} sm:w-auto sm:shrink`.trim(),
            style: { ...child.props.style, scrollSnapAlign: 'start' },
          });
        })}
      </div>

      <div className="mt-6 flex items-center justify-between gap-6 sm:hidden">
        <div className="flex gap-1.5">
          {children.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === active ? 'w-6 bg-azure-400' : 'w-1.5 bg-white/20 hover:bg-white/40'
              }`}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            disabled={!canPrev}
            aria-label="Previous"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-gray-300 transition-colors hover:bg-white/10 disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronLeft className="h-[18px] w-[18px]" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            disabled={!canNext}
            aria-label="Next"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-gray-300 transition-colors hover:bg-white/10 disabled:pointer-events-none disabled:opacity-30"
          >
            <ChevronRight className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Carousel;
